import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { OpeningHours } from '../OpeningHours';
import { LinkButton } from 'components/LinkButton';
import { InfoIcon, PhoneIcon, StoreIcon } from '../../svg';
import { useGoogleMapsContext } from '../../context';
import { dataLayerStoreEvent } from '../../utils';
import { colors, icons } from '../../constants';
import { IAvailableStore } from '../../types';

const TitleContainer = styled.div``;
const Title = styled.h4``;
const Distance = styled.span``;
const Address = styled.div``;
const ButtonContainer = styled.div``;
const StyledLink = styled.a``;
const StoreNote = styled.div``;

interface IProps {
    className?: string;
    store: IAvailableStore;
    searchPosition: number;
    hideDistance?: boolean;
    hideOpeningHours?: boolean;
    isInfoWindow?: boolean;
}

const BaseStoreCard = ({
    className,
    hideDistance,
    hideOpeningHours,
    searchPosition,
    isInfoWindow,
    store,
    store: {
        address,
        addressFr,
        city,
        cityFr,
        distanceKm,
        openingHours,
        phone,
        storeUrl,
        storeUrlFr,
        storeAppointmentUrl,
        storeAppointmentUrlFr,
        storeName,
        storeNameFr,
        storeNote,
        storeNoteFr,
        specialHours,
        id: storeId,
    },
}: IProps) => {
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const { googleMapsRef } = useGoogleMapsContext();

    const setMarkerIcon = (storeId: string, iconUrl: string) => {
        const match = googleMapsRef.current.markers?.find(
            (m) => m.store.id === storeId
        );

        if (match) {
            match.marker.setIcon({ url: iconUrl });
        }
    };

    const handleMouseEnter = () => setMarkerIcon(storeId, icons.markerHover);
    const handleMouseLeave = () => setMarkerIcon(storeId, icons.marker);

    const isSpecialStoreNote =
        storeNote ===
        'We hebben tijdelijk aangepaste openingstijden. Bekijk ze hieronder.';
    const hideStoreNote = isSpecialStoreNote && !specialHours.length;
    const showStoreNote = storeNote && !hideStoreNote && !hideOpeningHours;

    const displayedAddress = language === 'fr' ? addressFr : address;
    const displayedCity = language === 'fr' ? cityFr : city;

    const eventNameAppointment = isInfoWindow
        ? 'map click - book appointment'
        : 'book appointment click';
    const eventNameDetails = isInfoWindow
        ? 'map click - store details'
        : 'store details click';

    const dataLayerEvent = (interaction: string) =>
        dataLayerStoreEvent(store, searchPosition, interaction);

    return (
        <div
            className={className}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <TitleContainer>
                <Title>{language === 'fr' ? storeNameFr : storeName}</Title>
                {!!distanceKm && !hideDistance && (
                    <Distance>{`${distanceKm.toFixed(1)} km ${t(
                        'fromYourLocation'
                    )}`}</Distance>
                )}
            </TitleContainer>
            <Address>
                {displayedAddress}, {displayedCity}
            </Address>
            <ButtonContainer>
                <StyledLink href={`tel:${phone}`}>
                    <PhoneIcon />
                    {phone}
                </StyledLink>
                <StyledLink
                    href={language === 'fr' ? storeUrlFr : storeUrl}
                    onClick={() => dataLayerEvent(eventNameDetails)}
                >
                    <StoreIcon />
                    {t('viewDetails')}
                </StyledLink>
            </ButtonContainer>
            <OpeningHours
                hideOpeningHours={hideOpeningHours}
                openingHours={openingHours}
                specialHours={specialHours}
                onOpeningHoursClick={() =>
                    !isInfoWindow && dataLayerEvent('opening times click')
                }
            />
            {showStoreNote && (
                <StoreNote>
                    <InfoIcon />
                    <span>{language === 'fr' ? storeNoteFr : storeNote}</span>
                </StoreNote>
            )}
            <LinkButton
                href={
                    language === 'fr'
                        ? storeAppointmentUrlFr
                        : storeAppointmentUrl
                }
                title={t('makeAppointment')}
                onClick={() => dataLayerEvent(eventNameAppointment)}
            />
        </div>
    );
};

export const StoreCard = styled(BaseStoreCard)`
    border-radius: 5px;
    padding: 1rem;
    color: ${colors.black};
    font-size: 16px;
    transition: border 0.2s ease-in-out;
    border: 1px solid ${colors.greyBorder};

    ${TitleContainer} {
        column-gap: 1rem;
        display: flex;
        justify-content: space-between;
    }

    ${Title} {
        font-size: 1.125rem;
        font-weight: 500;
        line-height: 1.5;
    }

    ${Distance} {
        font-size: 0.875rem;
        line-height: 2;
        white-space: nowrap;
        color: ${colors.greyDark};
    }

    ${Address} {
        font-size: 0.875rem;
        line-height: 1.71;
        margin-bottom: 1rem;
        color: ${colors.greyDark};
    }

    ${ButtonContainer} {
        line-height: 1.71;
        margin-bottom: 1.4rem;
    }

    ${StyledLink} {
        align-items: center;
        display: flex;
        gap: 0.8rem;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.71;
        margin-bottom: 4px;
        color: ${colors.orange};

        &:hover {
            text-decoration: underline;
        }
    }

    ${StoreNote} {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        font-size: 0.875rem;
        line-height: 1.71;
        color: ${colors.blueDark};
        background-color: ${colors.infoAlert};
        border: 1px solid ${colors.blueDark};
        border-radius: 5px;
        padding: 16px;
        margin: 1rem 0;
        width: 100%;
    }

    ${InfoIcon} {
        fill: ${colors.blueDark};
        height: 24px;
        width: 24px;
        flex-shrink: 0;
    }

    ${PhoneIcon}, ${StoreIcon} {
        height: 1rem;
        width: 1rem;
    }

    ${LinkButton} {
        margin-top: 1.4rem;
        width: 100%;
    }
`;
